/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ComponentProps, type ReactElement, type Ref, Suspense, use, useEffect, useState } from 'react';
import { Await } from 'react-router';
import type { ShopperSearch } from '@/scapi';
import type { ProductUrlMap } from '@/lib/product/product-utils';
import { useDeferredRender, useDeferredRenderSequence } from '@/hooks/use-deferred-render';
import ProductGrid from './grid';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

/** The subset of ProductGrid props shared across the deferred phases (everything but the per-phase data). */
type SharedGridProps = Omit<
    ComponentProps<typeof ProductGrid>,
    'nonCritical' | 'resolvedProductPaths' | 'skeletonCount' | 'reservationCount'
>;

const MAX_INITIAL_VISIBLE_TILES = 8;
// RGBARRY-CUSTOMIZATION (perf): keep each mobile post-LCP React commit to one two-column row pair.
const NON_CRITICAL_BATCH_SIZE = 4;

function ProgressiveGrid({
    gridProps,
    nonCritical,
    progressivelyRender,
    renderedAdditionalBatches,
    resolvedProductPaths,
    onProductsResolved,
}: {
    gridProps: SharedGridProps;
    nonCritical: ProductSearchHit[];
    progressivelyRender: boolean;
    renderedAdditionalBatches: number;
    resolvedProductPaths?: ProductUrlMap;
    onProductsResolved: (count: number) => void;
}): ReactElement {
    const visibleCount = progressivelyRender
        ? Math.min(nonCritical.length, NON_CRITICAL_BATCH_SIZE * (renderedAdditionalBatches + 1))
        : nonCritical.length;
    const remainingCount = nonCritical.length - visibleCount;
    const criticalCount = gridProps.critical?.length ?? 0;
    const visibleSkeletonCount = Math.min(
        remainingCount,
        Math.max(0, MAX_INITIAL_VISIBLE_TILES - criticalCount - visibleCount)
    );

    useEffect(() => {
        if (progressivelyRender) {
            onProductsResolved(nonCritical.length);
        }
    }, [nonCritical.length, onProductsResolved, progressivelyRender]);

    return (
        <ProductGrid
            {...gridProps}
            appendPending={gridProps.appendPending || (progressivelyRender && remainingCount > 0)}
            nonCritical={nonCritical.slice(0, visibleCount)}
            skeletonCount={visibleSkeletonCount}
            reservationCount={remainingCount - visibleSkeletonCount}
            resolvedProductPaths={resolvedProductPaths}
        />
    );
}

/**
 * RGBARRY-CUSTOMIZATION (W-23875749): reads the resolver's `productId -> canonical path` promise and renders the
 * grid with the resolved map. Kept in its own inner Suspense so a slow resolve only delays the (already-deferred)
 * non-critical tiles behind their skeleton — never the above-the-fold critical tiles. The promise is guaranteed
 * non-rejecting (see the `.catch` at pin time below), so `use()` here can't surface an error to the boundary.
 */
function ResolvedGrid({
    urls,
    gridProps,
    nonCritical,
    progressivelyRender,
    renderedAdditionalBatches,
    onProductsResolved,
}: {
    urls: Promise<ProductUrlMap>;
    gridProps: SharedGridProps;
    nonCritical: ProductSearchHit[];
    progressivelyRender: boolean;
    renderedAdditionalBatches: number;
    onProductsResolved: (count: number) => void;
}): ReactElement {
    const resolvedProductPaths = use(urls);
    return (
        <ProgressiveGrid
            gridProps={gridProps}
            nonCritical={nonCritical}
            progressivelyRender={progressivelyRender}
            renderedAdditionalBatches={renderedAdditionalBatches}
            resolvedProductPaths={resolvedProductPaths}
            onProductsResolved={onProductsResolved}
        />
    );
}

/**
 * Wraps ProductGrid with three-phase deferred rendering for non-critical products.
 *
 * Why: Mounting a Suspense boundary on initial render forces React to retain the entire fallback
 * tree in memory and blocks the main thread while it reconciles the pending subtree — even if the
 * user hasn't scrolled to that content yet. This hurts LCP and inflates TBT. By deferring the
 * Suspense mount until after an idle callback, the critical (above-the-fold) tiles paint without
 * contention, then the non-critical tiles stream in once the browser is idle.
 *
 * Additionally, extracting this wrapper from ProductGrid itself keeps error handling explicit and
 * consistent across all routes: the error fallback always preserves critical tiles, rather than
 * each route reimplementing its own Suspense/Await/error pattern.
 *
 * Phase 1 (Pre-Idle): Show critical tiles + lightweight skeletons without mounting the Suspense
 * boundary. This keeps the initial render minimal, reducing TBT and improving LCP.
 *
 * Phase 2 (Post-Idle, Pending): Idle callback fires, Suspense boundary mounts with skeleton fallback.
 *
 * Phase 3 (Resolved): Non-critical products rendered via ProductGrid.
 */
export default function DeferredProductGrid({
    critical,
    nonCritical,
    appended,
    firstNewIndex,
    firstNewItemRef,
    appendPending,
    nonCriticalCount = 0,
    progressivelyRender = false,
    hasRefinementsPanel,
    handleProductClick,
    topCategoryName,
    isLoading,
    errorElement,
    resolvedProductPaths,
    enableRepresentedPrices,
    preferSearchRepresentedVariant,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable,
}: {
    critical?: ProductSearchHit[];
    nonCritical: Promise<ProductSearchHit[]>;
    /**
     * Products appended after the initial page via the "Load more" control. Rendered
     * inside the same grid container so critical, non-critical, and appended tiles flow as one grid.
     */
    appended?: ProductSearchHit[];
    /** Index within `appended` of the first tile of the most recent batch — receives `firstNewItemRef`. */
    firstNewIndex?: number | null;
    /** Ref attached to the first newly appended tile so focus can move to it after a "load more". */
    firstNewItemRef?: Ref<HTMLDivElement>;
    /** Whether a "load more" fetch is in flight — sets `aria-busy` on the grid for assistive tech. */
    appendPending?: boolean;
    nonCriticalCount?: number;
    /** Split resolved non-critical products into small idle-frame commits (enabled for mobile listing routes). */
    progressivelyRender?: boolean;
    hasRefinementsPanel?: boolean;
    handleProductClick?: (product: ProductSearchHit) => void;
    topCategoryName?: string;
    isLoading?: boolean;
    errorElement?: ReactElement;
    /**
     * RGBARRY-CUSTOMIZATION (W-23875749): promise of the resolver's `productId -> canonical path` map for the
     * first page's hits, produced by the PLP loader off the (already-deferred) non-critical fetch. Read inside
     * the non-critical Suspense boundary so it never blocks critical LCP; when omitted, tiles use the slugify path.
     */
    resolvedProductPaths?: Promise<ProductUrlMap>;
    /**
     * RGBARRY-CUSTOMIZATION: forwarded to `ProductGrid` → `ProductTileProvider` to opt this grid's master
     * tiles into showing their represented (face-out) variant price. See `resource.represented-prices.ts`.
     */
    enableRepresentedPrices?: boolean;
    /**
     * RGBARRY-CUSTOMIZATION: use each master hit's query-matching represented variant as its
     * face-out selection. Forwarded through ProductGrid to ProductTileProvider.
     */
    preferSearchRepresentedVariant?: boolean;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}): ReactElement {
    const shouldRenderNonCritical = useDeferredRender(nonCriticalCount > 0);
    const [resolvedNonCriticalCount, setResolvedNonCriticalCount] = useState(0);
    const additionalBatchCount = progressivelyRender
        ? Math.max(0, Math.ceil(resolvedNonCriticalCount / NON_CRITICAL_BATCH_SIZE) - 1)
        : 0;
    const renderedAdditionalBatches = useDeferredRenderSequence(shouldRenderNonCritical ? additionalBatchCount : 0, {
        idleTimeout: 750,
        // Safari has no requestIdleCallback. Leave breathing room between fallback commits instead of
        // queuing every batch in consecutive animation frames on the primary mobile Safari path.
        fallbackTimeout: 100,
    });
    // RGBARRY-CUSTOMIZATION: reserve every deferred tile to prevent the grid/footer jump, but preserve
    // PR #219's pre-idle render budget by mounting full skeletons only through the first eight visible slots.
    const visibleSkeletonCount = Math.min(
        nonCriticalCount,
        Math.max(0, MAX_INITIAL_VISIBLE_TILES - (critical?.length ?? 0))
    );
    const reservationCount = Math.max(0, nonCriticalCount - visibleSkeletonCount);

    // Pin the non-critical promise to its first reference for the lifetime of this component
    // instance. Without pinning, any non-navigating loader revalidation (e.g. a useFetcher.submit
    // from a tile-level mutation like wishlist-add) returns a fresh promise from the loader, which
    // <Await> treats as a new resolve and re-suspends — unmounting every tile (and any in-flight
    // fetcher whose owning component lives inside). Routes invalidate this pinned reference by
    // remounting via `key={productGridDataKey}` whenever URL identity changes (pagination, sort,
    // refine), which is the only time we actually want fresh non-critical data.
    const [pinnedNonCritical] = useState(() => nonCritical);

    // Pin the resolved-paths promise alongside the non-critical one (same rationale — a revalidation must not
    // re-suspend the grid). Guard it so a stream-abort rejection resolves to an empty map instead of throwing
    // out of `use()`; an empty map degrades every tile to its slugify path, never a broken render.
    const [pinnedResolvedPaths] = useState<Promise<ProductUrlMap> | undefined>(() =>
        resolvedProductPaths ? resolvedProductPaths.catch(() => ({}) as ProductUrlMap) : undefined
    );

    const gridProps = {
        critical,
        appended,
        firstNewIndex,
        firstNewItemRef,
        appendPending,
        hasRefinementsPanel,
        handleProductClick,
        topCategoryName,
        isLoading,
        enableRepresentedPrices,
        preferSearchRepresentedVariant,
        // @sfdc-extension-line SFDC_EXT_BOPIS
        showPickupAvailable,
    };

    // Phase 1: Pre-idle — show critical tiles + skeleton placeholders.
    // (`appended` is empty here — the shopper can only load more once the initial page has resolved.)
    if (!shouldRenderNonCritical) {
        return <ProductGrid {...gridProps} skeletonCount={visibleSkeletonCount} reservationCount={reservationCount} />;
    }

    // Phase 2 & 3: Post-idle — mount Suspense + Await, resolve non-critical tiles.
    // When the promise rejects, render critical tiles alongside the errorElement so
    // above-the-fold products remain visible even when non-critical loading fails.
    const errorFallback = errorElement ? (
        <>
            <ProductGrid {...gridProps} />
            {errorElement}
        </>
    ) : undefined;

    return (
        <Suspense
            fallback={
                <ProductGrid {...gridProps} skeletonCount={visibleSkeletonCount} reservationCount={reservationCount} />
            }>
            <Await resolve={pinnedNonCritical} errorElement={errorFallback}>
                {(products: ProductSearchHit[]) =>
                    pinnedResolvedPaths ? (
                        // Inner Suspense: while the resolver map streams, show the grid on the slugify path;
                        // swap to resolved URLs when it arrives. Keeps resolution off the non-critical hits' path.
                        <Suspense
                            fallback={
                                <ProgressiveGrid
                                    gridProps={gridProps}
                                    nonCritical={products}
                                    progressivelyRender={progressivelyRender}
                                    renderedAdditionalBatches={renderedAdditionalBatches}
                                    onProductsResolved={setResolvedNonCriticalCount}
                                />
                            }>
                            <ResolvedGrid
                                urls={pinnedResolvedPaths}
                                gridProps={gridProps}
                                nonCritical={products}
                                progressivelyRender={progressivelyRender}
                                renderedAdditionalBatches={renderedAdditionalBatches}
                                onProductsResolved={setResolvedNonCriticalCount}
                            />
                        </Suspense>
                    ) : (
                        <ProgressiveGrid
                            gridProps={gridProps}
                            nonCritical={products}
                            progressivelyRender={progressivelyRender}
                            renderedAdditionalBatches={renderedAdditionalBatches}
                            onProductsResolved={setResolvedNonCriticalCount}
                        />
                    )
                }
            </Await>
        </Suspense>
    );
}
